import { useEffect } from 'react';
import NavBar from '../../components/feature/NavBar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://zjfitservices.com';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy | ZJF IT Services LLC',
      description: 'Privacy Policy for ZJF IT Services LLC. Learn how we collect, use, and protect your personal information through contact forms, partnership submissions, and website analytics.',
      url: `${siteUrl}/privacy-policy`,
      inLanguage: 'en-US',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${siteUrl}/privacy-policy` },
        ],
      },
      isPartOf: {
        '@type': 'WebSite',
        name: 'ZJF IT Services LLC',
        url: siteUrl,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title="Privacy Policy | ZJF IT Services LLC — Data Protection & Privacy"
        description="Privacy Policy for ZJF IT Services LLC. Learn how we collect, use, and protect your personal information through our contact forms, partnership submissions, and website analytics."
        keywords="privacy policy, data protection, ZJF IT Services privacy, personal information, GDPR compliance"
        canonical={`${siteUrl}/privacy-policy`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <NavBar />
        
        <main className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Privacy Policy</h1>
              <p className="text-zinc-400 text-sm">
                <strong>Effective Date:</strong> January 1, 2025
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
            </div>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-zinc-300 leading-relaxed">
                At <strong className="text-orange-500">ZJ Fit Services</strong>, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-orange-500">1.1 Personal Information</h3>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-6">
                <li>Fill out our contact form (name, email, phone number, message)</li>
                <li>Submit a partnership inquiry form (company name, contact details, business information)</li>
                <li>Subscribe to our newsletter or email updates</li>
                <li>Communicate with us via email or phone</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-orange-500">1.2 Automatically Collected Information</h3>
              <p className="text-zinc-300 leading-relaxed mb-4">
                When you visit our website, we automatically collect certain information about your device and browsing behavior, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4">
                <li>IP address and browser type</li>
                <li>Operating system and device information</li>
                <li>Pages visited, time spent on pages, and navigation patterns</li>
                <li>Referring website and search terms used</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">2. How We Use Your Information</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4">
                <li><strong>Service Delivery:</strong> To respond to your inquiries, process partnership requests, and provide our fitness facility design and consulting services</li>
                <li><strong>Communication:</strong> To send you updates, newsletters, marketing materials, and important service announcements</li>
                <li><strong>Website Improvement:</strong> To analyze website usage, improve user experience, and optimize our content and services</li>
                <li><strong>Analytics:</strong> To understand visitor behavior through Google Analytics and similar tools</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              </ul>
            </section>

            {/* Google Analytics */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">3. Google Analytics</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We use Google Analytics to collect and analyze information about how visitors use our website. Google Analytics uses cookies to track your interactions with our site, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Pages viewed and time spent on each page</li>
                <li>Traffic sources and referral information</li>
                <li>Geographic location (city/country level)</li>
                <li>Device and browser information</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                Google Analytics data is anonymized and aggregated. You can opt out of Google Analytics tracking by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400 underline">Google Analytics Opt-out Browser Add-on</a>.
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">4. Cookies and Tracking Technologies</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and user behavior</li>
                <li>Improve website functionality and performance</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                You can control cookie settings through your browser preferences. However, disabling cookies may limit certain features of our website.
              </p>
            </section>

            {/* Data Storage and Security */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">5. Data Storage and Security</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li>Secure data transmission using SSL/TLS encryption</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication protocols</li>
                <li>Secure data storage with reputable third-party service providers</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">6. Third-Party Services</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                We may share your information with trusted third-party service providers who assist us in operating our website and delivering our services, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
                <li><strong>Email Service Providers:</strong> For sending newsletters and communications</li>
                <li><strong>Form Processing Services:</strong> For handling contact and partnership form submissions</li>
                <li><strong>Hosting Providers:</strong> For website hosting and data storage</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                These third parties are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">7. Data Retention</h2>
              <p className="text-zinc-300 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Contact form submissions and partnership inquiries are typically retained for up to 3 years for business and legal purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">8. Your Rights and Choices</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-zinc-300 ml-4 mb-4">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed">
                To exercise any of these rights, please contact us at <a href="mailto:info@zjfitservices.com" className="text-orange-500 hover:text-orange-400 underline">info@zjfitservices.com</a>.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">9. Children's Privacy</h2>
              <p className="text-zinc-300 leading-relaxed">
                Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete it.
              </p>
            </section>

            {/* International Users */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">10. International Users</h2>
              <p className="text-zinc-300 leading-relaxed">
                Our website is operated from the United States. If you are accessing our website from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where data protection laws may differ from those in your country.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">11. Changes to This Privacy Policy</h2>
              <p className="text-zinc-300 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold mb-4 text-white">12. Contact Us</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <p className="text-zinc-300 mb-2"><strong className="text-white">ZJ Fit Services</strong></p>
                <p className="text-zinc-300 mb-2">Email: <a href="mailto:info@zjfitservices.com" className="text-orange-500 hover:text-orange-400 underline">info@zjfitservices.com</a></p>
                <p className="text-zinc-300 mb-2">Phone: <a href="tel:+19084442222" className="text-orange-500 hover:text-orange-400 underline">(908) 444-2222</a></p>
                <p className="text-zinc-300">Location: New Jersey, United States</p>
              </div>
            </section>

            {/* Consent */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <p className="text-zinc-300 leading-relaxed">
                By using our website and services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this policy, please do not use our website or provide us with any personal information.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}